/**
 * Calm Command — biome-specific scene builders & renderers.
 *
 * Extracted verbatim from CalmCommand.jsx to shrink the game component and
 * enable each biome's silhouette rendering to be unit-tested in isolation.
 *
 * `buildScene(theme, biome, W, H)` returns a plain-data object describing the
 * static background silhouette (deterministic per biome via seededRng).
 * `drawScene(ctx, scene, biome, W, H, horY, t)` paints that silhouette onto
 * the canvas — pure canvas draws, no state.
 *
 * Six supported themes: `city`, `forest`, `desert`, `coast`, `mountains`, `prism`.
 */
import { TAU, HORIZON_Y_FRAC, ANCHOR_Y_FRAC, seededRng } from './constants';

export function buildScene(theme, biome, W, H) {
  const rng = seededRng(biome.id * 9973 + 1);
  const horY = H * HORIZON_Y_FRAC;
  const anchorY = H * ANCHOR_Y_FRAC;
  const band = anchorY - horY;

  if (theme === 'city') {
    const buildings = [];
    let x = -8;
    while (x < W + 16) {
      const w = 22 + rng() * 36;
      const h = 26 + rng() * (band + 30);
      const windows = [];
      const rows = Math.floor(h / 8);
      const cols = Math.max(1, Math.floor(w / 8));
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          if (rng() < 0.55) windows.push({ x: 2 + c * 6.5, y: 4 + r * 7, on: rng() < 0.7 });
        }
      }
      buildings.push({ x, w, h, windows, tint: rng() < 0.5 ? biome.accent : biome.glow });
      x += w + 2 + rng() * 6;
    }
    return { theme, buildings };
  }

  if (theme === 'forest') {
    const trees = [];
    let x = -8;
    while (x < W + 16) {
      const w = 16 + rng() * 22;
      const h = 20 + rng() * (band + 22);
      trees.push({ x, w, h, glow: rng() < 0.35 });
      x += w * 0.4 + 2 + rng() * 6;
    }
    return { theme, trees };
  }

  if (theme === 'desert') {
    const dune1 = Array.from({ length: 24 }, (_, i) => ({
      x: (i / 23) * W,
      y: horY - 4 + Math.sin(i * 0.5 + rng() * 2) * 6 - rng() * 8,
    }));
    const dune2 = Array.from({ length: 20 }, (_, i) => ({
      x: (i / 19) * W,
      y: horY - 18 + Math.sin(i * 0.35 + 1.4) * 10 - rng() * 4,
    }));
    const sunX = W * (0.65 + rng() * 0.2);
    const sunY = horY - 34 - rng() * 10;
    return { theme, dune1, dune2, sunX, sunY };
  }

  if (theme === 'coast') {
    const waveLines = Array.from({ length: 6 }, (_, i) => ({
      y: horY + 4 + i * 6,
      dashOffset: rng() * 20,
    }));
    const rockX = W * (0.2 + rng() * 0.3);
    return { theme, waveLines, rockX };
  }

  if (theme === 'mountains') {
    const back = Array.from({ length: 12 }, (_, i) => ({
      x: (i / 11) * W,
      y: horY - (24 + rng() * 24),
    }));
    const front = Array.from({ length: 10 }, (_, i) => ({
      x: (i / 9) * W,
      y: horY - (10 + rng() * 22),
      snow: rng() < 0.4,
    }));
    return { theme, back, front };
  }

  if (theme === 'prism') {
    const shards = [];
    const n = 8;
    for (let i = 0; i < n; i++) {
      const cx = ((i + 0.5) / n) * W;
      const h = 22 + rng() * (band + 40);
      const w = 12 + rng() * 18;
      const hueSet = ['#7dfaff', '#ff5cff', '#39ffcf', '#ffcf3a', '#ff3a7d', '#c17dff'];
      shards.push({ cx, h, w, color: hueSet[i % hueSet.length] });
    }
    return { theme, shards };
  }

  return { theme: 'none' };
}

export function drawScene(ctx, scene, biome, W, H, horY, t) {
  if (!scene || scene.theme === 'none') return;

  if (scene.theme === 'city') {
    for (const b of scene.buildings) {
      const top = horY - b.h;
      ctx.fillStyle = '#050514';
      ctx.fillRect(b.x, top, b.w, b.h);
      ctx.strokeStyle = `${b.tint}88`;
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(b.x, top); ctx.lineTo(b.x + b.w, top); ctx.stroke();
      ctx.shadowColor = b.tint;
      ctx.shadowBlur = 8;
      ctx.fillStyle = b.tint;
      ctx.fillRect(b.x, top, b.w, 1.5);
      ctx.shadowBlur = 0;
      for (const w of b.windows) {
        const wx = b.x + w.x;
        const wy = top + w.y;
        if (wy > horY - 2) continue;
        ctx.fillStyle = w.on ? (b.tint) : 'rgba(255,255,255,0.08)';
        ctx.globalAlpha = w.on ? 0.85 : 0.4;
        ctx.fillRect(wx, wy, 2.5, 2.5);
      }
      ctx.globalAlpha = 1;
    }
    return;
  }

  if (scene.theme === 'forest') {
    for (const tree of scene.trees) {
      const baseY = horY;
      const topY = horY - tree.h;
      ctx.fillStyle = '#04120a';
      ctx.beginPath();
      ctx.moveTo(tree.x + tree.w / 2, topY);
      ctx.lineTo(tree.x, baseY);
      ctx.lineTo(tree.x + tree.w, baseY);
      ctx.closePath();
      ctx.fill();
      if (tree.glow) {
        ctx.shadowColor = biome.accent;
        ctx.shadowBlur = 8;
        ctx.fillStyle = biome.accent;
        ctx.fillRect(tree.x + tree.w / 2 - 1, topY - 1, 2, 3);
        ctx.shadowBlur = 0;
      }
    }
    const g = ctx.createLinearGradient(0, horY - 30, 0, horY);
    g.addColorStop(0, 'rgba(0,0,0,0)');
    g.addColorStop(1, `${biome.accent}22`);
    ctx.fillStyle = g;
    ctx.fillRect(0, horY - 30, W, 30);
    return;
  }

  if (scene.theme === 'desert') {
    ctx.save();
    ctx.shadowColor = biome.glow;
    ctx.shadowBlur = 20;
    ctx.fillStyle = biome.glow;
    ctx.beginPath();
    ctx.arc(scene.sunX, scene.sunY, 12, 0, TAU);
    ctx.fill();
    ctx.restore();
    ctx.fillStyle = '#2a1204';
    ctx.beginPath();
    ctx.moveTo(0, horY);
    for (const d of scene.dune2) ctx.lineTo(d.x, d.y);
    ctx.lineTo(W, horY);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#3a1a06';
    ctx.beginPath();
    ctx.moveTo(0, horY);
    for (const d of scene.dune1) ctx.lineTo(d.x, d.y);
    ctx.lineTo(W, horY);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = `${biome.accent}66`;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(scene.dune1[0].x, scene.dune1[0].y);
    for (const d of scene.dune1) ctx.lineTo(d.x, d.y);
    ctx.stroke();
    return;
  }

  if (scene.theme === 'coast') {
    const waterTop = horY - 6;
    const g = ctx.createLinearGradient(0, waterTop, 0, horY + 20);
    g.addColorStop(0, `${biome.accent}22`);
    g.addColorStop(1, `${biome.glow}55`);
    ctx.fillStyle = g;
    ctx.fillRect(0, waterTop, W, 26);
    ctx.strokeStyle = `${biome.accent}66`;
    for (const line of scene.waveLines) {
      ctx.setLineDash([8, 12]);
      ctx.lineDashOffset = -((t * 20) + line.dashOffset);
      ctx.beginPath();
      ctx.moveTo(0, line.y);
      ctx.lineTo(W, line.y);
      ctx.stroke();
    }
    ctx.setLineDash([]);
    const lhX = scene.rockX;
    const lhY = horY - 28;
    ctx.fillStyle = '#0a0210';
    ctx.beginPath();
    ctx.moveTo(lhX - 14, horY);
    ctx.lineTo(lhX - 8, lhY);
    ctx.lineTo(lhX + 8, lhY);
    ctx.lineTo(lhX + 14, horY);
    ctx.closePath();
    ctx.fill();
    ctx.fillRect(lhX - 4, lhY - 20, 8, 24);
    ctx.save();
    ctx.shadowColor = biome.glow;
    ctx.shadowBlur = 12;
    ctx.fillStyle = biome.glow;
    ctx.fillRect(lhX - 3, lhY - 26, 6, 6);
    ctx.restore();
    return;
  }

  if (scene.theme === 'mountains') {
    ctx.fillStyle = '#180208';
    ctx.beginPath();
    ctx.moveTo(0, horY);
    for (const p of scene.back) ctx.lineTo(p.x, p.y);
    ctx.lineTo(W, horY);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#2a0410';
    ctx.beginPath();
    ctx.moveTo(0, horY);
    for (const p of scene.front) ctx.lineTo(p.x, p.y);
    ctx.lineTo(W, horY);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#f7d8e0';
    for (const p of scene.front) {
      if (!p.snow) continue;
      ctx.beginPath();
      ctx.moveTo(p.x - 4, p.y + 6);
      ctx.lineTo(p.x, p.y);
      ctx.lineTo(p.x + 4, p.y + 6);
      ctx.closePath();
      ctx.fill();
    }
    ctx.strokeStyle = `${biome.accent}55`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, horY);
    for (const p of scene.front) ctx.lineTo(p.x, p.y);
    ctx.stroke();
    return;
  }

  if (scene.theme === 'prism') {
    for (const s of scene.shards) {
      const top = horY - s.h;
      ctx.save();
      ctx.shadowColor = s.color;
      ctx.shadowBlur = 12;
      ctx.fillStyle = `${s.color}44`;
      ctx.strokeStyle = s.color;
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(s.cx, top);
      ctx.lineTo(s.cx + s.w / 2, top + s.h * 0.35);
      ctx.lineTo(s.cx + s.w / 3, horY);
      ctx.lineTo(s.cx - s.w / 3, horY);
      ctx.lineTo(s.cx - s.w / 2, top + s.h * 0.35);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.strokeStyle = `${s.color}88`;
      ctx.beginPath();
      ctx.moveTo(s.cx, top);
      ctx.lineTo(s.cx, horY);
      ctx.stroke();
      ctx.restore();
    }
  }
}
